import { request } from '@/shared/api/request'
import { isL1Error } from '@/shared/api/l1-error'
import type { BindCourseBody, BindCourseResult, DesignNode, StageConfirmResult, TeachingDesign } from './types-design'
import { etagHeaders, workItemPath } from './paths'

export function bindCourse(
  workItemId: number,
  body: BindCourseBody,
): Promise<BindCourseResult> {
  return request.post(workItemPath(workItemId, '/bind-course'), body, {
    params: body.force_new ? { force_new: true } : undefined,
  })
}

export function getDesign(workItemId: number): Promise<TeachingDesign> {
  return request.get(workItemPath(workItemId, '/design'))
}

export function putStage(
  workItemId: number,
  stageN: number,
  payload: Record<string, unknown>,
  etag?: string | null,
): Promise<{ etag: string; stage: number }> {
  return request.put(
    workItemPath(workItemId, `/design/stages/${stageN}`),
    { payload, etag },
    etagHeaders(etag),
  )
}

export function confirmStage(
  workItemId: number,
  stageN: number,
  payload?: Record<string, unknown> | null,
  etag?: string | null,
): Promise<StageConfirmResult> {
  return request.post(
    workItemPath(workItemId, `/design/stages/${stageN}/confirm`),
    { payload, etag },
    etagHeaders(etag),
  )
}

export function restartDesign(workItemId: number): Promise<{ etag?: string }> {
  return request.post(workItemPath(workItemId, '/design/restart'))
}

export function rebindKnowledge(workItemId: number): Promise<{ etag?: string }> {
  return request.post(workItemPath(workItemId, '/design/rebind-knowledge'))
}

export function getNode(workItemId: number, nodeKey: string): Promise<DesignNode> {
  return request.get(workItemPath(workItemId, `/nodes/${encodeURIComponent(nodeKey)}`))
}

export async function getNodeOptional(
  workItemId: number,
  nodeKey: string,
): Promise<DesignNode | null> {
  try {
    return await request.get(workItemPath(workItemId, `/nodes/${encodeURIComponent(nodeKey)}`), {
      skipErrorToast: true,
    })
  } catch (err) {
    if (isL1Error(err) && err.code === 4040) return null
    throw err
  }
}

export function suggestCourseObjectives(
  workItemId: number,
): Promise<{ courseObjectives?: string; suggestions?: string[] }> {
  return request.post(workItemPath(workItemId, '/suggest-course-objectives'))
}

export function suggestTargetJob(
  workItemId: number,
  options?: { preserveLevel?: string },
): Promise<{
  suggestions?: string[]
  jobLevel?: string
  jobTitle?: string
}> {
  const body = options?.preserveLevel ? { preserveLevel: options.preserveLevel } : undefined
  return request.post(workItemPath(workItemId, '/suggest-target-job'), body)
}

export function matchKnowledgePoints(
  workItemId: number,
  body: {
    stepText: string
    textKind?: string
    includeExcerpts?: boolean
  },
): Promise<{ matchedKps: string[]; mappings?: Array<{ knowledgePoint: string; excerpt: string }> }> {
  return request.post(workItemPath(workItemId, '/match-knowledge-points'), body)
}

export function generateTheoryPoints(
  workItemId: number,
  body: {
    situationTitle: string
    situationGoal?: string
    typicalWorkTask?: string
    keySteps?: string[]
  },
): Promise<{ theoryPoints: Array<{ title: string; content: string; knowledgePoints: string[] }> }> {
  return request.post(workItemPath(workItemId, '/generate-theory-points'), body)
}
